/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { CircularProgress, Link, Stack, Tooltip, Typography } from "@mui/material";
import Chip from "@mui/material/Chip";
import { a11yTabProps } from "../../components/tabs/tabs";
import TableModal from "../../components/modal/TableModal";
import { Assignment, ExpandedWorkOrder, Material, Path, ServiceLocation, WorkOrder } from "../../utils/Interfaces";
import { ImportWalmartWorkOrderModal } from "./ImportWalmartWorkOrderModal";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { selectExistingSites, selectExistingSitesLoaded } from "../office-work-sites/workSitesSlice";
import { updateWorkOrders } from "./workOrdersFunctions";
import { updateSites } from "../office-work-sites/workSitesFunctions";
import { TabDataPage } from "./TabDataPage";
import { selectExistingWorkOrders, selectExistingWorkOrdersLoaded } from "./workOrdersSlice";
import CreateWorkOrderModal from "./CreateWorkOrderModal";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Nullable } from "../../utils/Interfaces";
import { useSearchParams } from "react-router-dom";
import { workOrdersStatusTitles } from "../../utils/Constants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import InfoModal from "../../components/modal/InfoModal";

const WorkOrdersList = () => {
    type ManualMaterial = { trip_number: number; write_in: Nullable<string> };

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const existingSites: ServiceLocation[] = useAppSelector(selectExistingSites);
    const existingWorkOrders: WorkOrder[] = useAppSelector(selectExistingWorkOrders);

    const existingSitesLoaded: boolean = useAppSelector(selectExistingSitesLoaded);
    const exisingWorkOrdersLoaded: boolean = useAppSelector(selectExistingWorkOrdersLoaded);

    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [importModalOpen, setImportModalOpen] = useState<boolean>(false);

    const [partsModalOpen, setPartsModalOpen] = useState<boolean>(false);

    const [activeTitle, setActiveTitle] = useState<string>("");
    const [activeDescription, setActiveDescription] = useState<string>("");
    const [descriptionModalOpen, setDescriptionModalOpen] = useState<boolean>(false);

    const [selectedMaterialsWriteIn, setSelectedMaterialsWriteIn] = useState<ManualMaterial[]>([]);
    const [selectedPartsUsed, setSelectedPartsUsed] = useState<Material[]>([]);

    const [searchParams] = useSearchParams();
    const status: Nullable<string> = searchParams.get("status");

    /**
     * Returns a lost of materials as a list contained within a DOM container.
     */
    function getManualMaterials(): JSX.Element {
        if (selectedMaterialsWriteIn.length === 0) {
            return <Typography>--</Typography>;
        }

        let concat: JSX.Element[] = [];

        for (let i = 0; i < selectedMaterialsWriteIn.length; i++) {
            concat.push(
                <Box sx={{ mt: 2 }}>
                    <Typography fontWeight="bold">{`Trip #${selectedMaterialsWriteIn[i].trip_number}`}</Typography>
                    <Typography variant="subtitle2">{`${selectedMaterialsWriteIn[i].write_in}\n`}</Typography>
                </Box>
            );
        }

        return (
            <Box>
                {concat.map((entry) => {
                    return entry;
                })}
            </Box>
        );
    }

    function statusToTabIndex(status: Nullable<string>): number {
        switch (status) {
            case "UN":
                return 0;
            case "IP":
                return 1;
            case "SB":
                return 2;
            case "CM":
                return 3;
            case "ALL":
                return 4;
            default:
                return 0;
        }
    }

    function tabIndexToStatus(index: number): string {
        switch (index) {
            case 0:
                return "UN";
            case 1:
                return "IP";
            case 2:
                return "SB";
            case 3:
                return "CM";
            case 4:
                return "ALL";
            default:
                return "UN";
        }
    }

    function getAssignees(workOrder: ExpandedWorkOrder): string {
        let assignments: Assignment[] = workOrder.assignments;

        let assignees: string = "";

        let existingAssignees: number[] = [];

        function filterAssignees(assignment: Assignment) {
            if (!existingAssignees.includes(assignment.technician.id)) {
                existingAssignees.push(assignment.technician.id);
                return true;
            }
            return false;
        }

        assignments.filter(filterAssignees).map((assignee: Assignment): string => {
            assignees += `${assignee.technician.first_name} ${assignee.technician.last_name}, `;
            return "";
        });

        if (assignments.length > 0) {
            assignees = assignees.substring(0, assignees.length - 2);
        }

        return assignees;
    }

    let breadcrumbPaths: Path[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Service", route: "/?section=office" },
    ];

    if (status) {
        let breadcrumbTitle: string = workOrdersStatusTitles[status as keyof typeof workOrdersStatusTitles];

        // Only add this as a breadcrumb item if it is a valid selection.
        if (breadcrumbTitle) {
            breadcrumbPaths.push({ text: `Work Orders - ${breadcrumbTitle}`, route: `/work-orders?status=${status}` });
        } else {
            breadcrumbPaths.push({ text: "Work Orders - Unassigned", route: "/work-orders?status=UN" });
        }
    } else {
        breadcrumbPaths.push({ text: "Work Orders - Unassigned", route: "/work-orders?status=UN" });
    }

    const idCol = {
        field: "id",
        headerName: "id",
        filterable: false,
        sortable: false,
    };

    const ticketCol = {
        field: "ticket_no",
        width: 150,
        headerName: "Work Order #",
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.ticket_no;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Link style={{ cursor: "pointer" }} onClick={() => navigate(`/work-orders/${params.row.id}`)}>
                    <Typography variant="overline" fontSize={14}>
                        {params.row.ticket_no}
                    </Typography>
                </Link>
            );
        },
    };

    const tripNumberCol = {
        field: "trip_number",
        width: 70,
        headerName: "Trip #",
        sortable: true,
        filterable: true,
    };

    const assigneeCol = {
        field: "assignee",
        headerName: "Assignees",
        width: 180,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return getAssignees(params.row);
        },
        renderCell: (params: GridRenderCellParams) => {
            let assignees: string = getAssignees(params.row);
            let leadAssignee: string = assignees;
            let assigneesSplit: string[] = assignees.split(",");

            leadAssignee = assigneesSplit[0];

            if (assigneesSplit.length > 1) {
                return (
                    <Tooltip title={assignees}>
                        <Link
                            onClick={() => {
                                setActiveTitle(`Assignees (${params.row.ticket_no})`);
                                setActiveDescription(assignees);
                                setDescriptionModalOpen(true);
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{ textOverflow: "ellipsis", width: 145, cursor: "pointer" }}
                            >
                                {`${leadAssignee}, +${assigneesSplit.length - 1}`}
                            </Typography>
                        </Link>
                    </Tooltip>
                );
            }

            return (
                <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 175 }}>
                    {leadAssignee}
                </Typography>
            );
        },
    };

    const cityCol = {
        field: "city",
        headerName: "City",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.site.city;
        },
        renderCell: (params: GridRenderCellParams) => {
            return <Typography>{params.row.site.city}</Typography>;
        },
    };

    const stateCol = {
        field: "state",
        headerName: "State",
        width: 70,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.site.state;
        },
        renderCell: (params: GridRenderCellParams) => {
            return <Typography>{params.row.site.state}</Typography>;
        },
    };

    const probDescCol = {
        field: "problem_description",
        headerName: "Problem Description",
        width: 200,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.problem_description;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={params.row.problem_description}>
                    <Link
                        onClick={() => {
                            setActiveTitle(`Problem Description (${params.row.ticket_no})`);
                            setActiveDescription(params.row.problem_description);
                            setDescriptionModalOpen(true);
                        }}
                    >
                        <Typography variant="subtitle2" noWrap sx={{ textOverflow: "ellipsis", width: 195, cursor: "pointer" }}>
                            {params.row.problem_description}
                        </Typography>
                    </Link>
                </Tooltip>
            );
        },
    };

    const dateSubmittedCol = {
        field: "date_submitted",
        headerName: "Date Submitted",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return new Date(params.row.date_submitted).getTime();
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Typography variant="subtitle1">
                    {params.row.date_submitted}
                </Typography>
            )
        }
    };

    const dateCompletedCol = {
        field: "date_completed",
        headerName: "Date Completed",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return new Date(params.row.date_completed).getTime();
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Typography variant="subtitle1">
                    {params.row.date_completed}
                </Typography>
            )
        }
    };

    const returnTripCol = {
        field: "return_trip_needed",
        headerName: "Return Trip?",
        width: 100,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.return_trip_needed ? "Yes" : "No";
        },
        renderCell: (params: GridRenderCellParams) => {
            let returnTripNeeded: boolean = params.row.return_trip_needed;
            return (
                <Stack direction="row" justifyContent="center" sx={{ width: 100 }}>
                    <Chip
                        label={returnTripNeeded ? "Yes" : "No"}
                        color={returnTripNeeded ? "error" : "success"}
                        style={{ fontWeight: "bold" }}
                    />
                </Stack>
            );
        },
    };

    const partsUsedCol = {
        field: "parts_used",
        headerName: "Parts Used",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
            let partsUsed: Material[] = params.row.parts_used;
            let manualPartsUsed: ManualMaterial[] = params.row.manual_parts_used;

            let totalLength: number = partsUsed.length + manualPartsUsed.length;

            if (totalLength > 0) {
                return (
                    <Link
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            setSelectedMaterialsWriteIn(params.row.manual_parts_used);
                            setSelectedPartsUsed(partsUsed);
                            setPartsModalOpen(true);
                        }}
                    >
                        View {totalLength} part{totalLength === 1 ? "" : "s"}
                    </Link>
                );
            }

            return <Chip label="None" color="info" style={{ fontWeight: "bold" }} />;
        },
    };

    const devToBeFixedCol = {
        field: "device_to_be_fixed",
        headerName: "Device to be Fixed",
        width: 200,
        sortable: true,
        filterable: true,
    };

    const companyCol = {
        field: "company",
        headerName: "Company",
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.site.company.name;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Link sx={{ cursor: "pointer" }} onClick={() => navigate(`/companies/${params.row.site.company.id}`)}>
                    {params.row.site.company.name}
                </Link>
            );
        },
    };

    const statusCol = {
        field: "status",
        headerName: "Status",
        type: "singleSelect",
        sortable: true,
        valueOptions: ["UN", "IP", "SB", "CM", "CN", "AR"],
        filterable: true,
    };

    const storeNoCol = {
        field: "store_no",
        width: 100,
        headerName: "Store #",
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.site.store_no;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Link style={{ cursor: "pointer" }} onClick={() => navigate(`/work-sites/${params.row.site.id}`)}>
                    <Typography variant="overline" fontSize={14}>
                        {params.row.site.store_no}
                    </Typography>
                </Link>
            );
        },
    };
    const dateReceivedCol = {
        field: "date_created",
        headerName: "Date Created",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridRenderCellParams) => {
            return new Date(params.row.date_created).getTime();
        },
        renderCell: (params: GridRenderCellParams) => {
            let dateStr = params.row.date_created;

            if (dateStr != null) {
                let date: Date = new Date(dateStr);

                // @ts-ignore
                let daysSinceReceived: number = (new Date() - date) / 86400000;

                let dateLabel = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

                if (daysSinceReceived < 6.0) {
                    // success, primary, error, secondary
                    return <Chip label={dateLabel} color="success" style={{ fontWeight: "bold" }} />;
                } else if (daysSinceReceived < 10.0) {
                    return <Chip label={dateLabel} color="primary" style={{ background: "#bfa600", fontWeight: "bold" }} />;
                }
                return <Chip label={dateLabel} color="error" style={{ fontWeight: "bold" }} />;
            } else {
                return <Chip label="N/A" color="secondary" style={{ fontWeight: "bold" }} />;
            }
        },
    };

    const unassignedColumns = [
        idCol,
        storeNoCol,
        cityCol,
        stateCol,
        ticketCol,
        dateReceivedCol,
        devToBeFixedCol,
        tripNumberCol,
        probDescCol,
    ];

    const inProgColumns = [
        idCol,
        storeNoCol,
        cityCol,
        stateCol,
        ticketCol,
        assigneeCol,
        tripNumberCol,
        dateReceivedCol,
        probDescCol,
    ];

    const submittedColumns = [
        idCol,
        storeNoCol,
        cityCol,
        stateCol,
        ticketCol,
        assigneeCol,
        returnTripCol,
        dateReceivedCol,
        dateSubmittedCol,
        probDescCol,
    ];

    const completedColumns = [
        idCol,
        storeNoCol,
        cityCol,
        stateCol,
        ticketCol,
        assigneeCol,
        partsUsedCol,
        dateCompletedCol,
        probDescCol,
    ];

    const allColumns = [
        idCol,
        storeNoCol,
        cityCol,
        stateCol,
        companyCol,
        ticketCol,
        assigneeCol,
        devToBeFixedCol,
        statusCol,
        dateReceivedCol,
        probDescCol,
    ];

    useEffect(() => {
        updateWorkOrders(dispatch, () => {});
        updateSites(dispatch, () => {});
    }, [dispatch]);

    const onClickAddNew = () => {
        setCreateModalOpen(true);
    };

    function tabDataPageFactory(status: string, tabIndex: number, columns: GridColDef<any, any, any>[]) {
        return (
            <TabDataPage
                index={tabIndex}
                columns={columns}
                data={existingWorkOrders.filter((workOrder) => (status === "ALL" ? true : workOrder.status === status))}
                handleImportModalOpen={() => setImportModalOpen(true)}
                onClickAddNew={onClickAddNew}
                indexValue={statusToTabIndex(status)}
                label={status}
            />
        );
    }

    return (
        <Box>
            <InfoModal
                title={activeTitle}
                description={activeDescription}
                open={descriptionModalOpen}
                handleClose={() => setDescriptionModalOpen(false)}
            />

            <TableModal<Material>
                title="Selected Parts Used"
                data={selectedPartsUsed}
                columns={[
                    { header: "Part Name", width: "50%", field: "name" },
                    { header: "P/N", width: "35%", field: "part_number" },
                    { header: "Amount", width: "15%", field: "amount" },
                ]}
                rowsPerPage={10}
                open={partsModalOpen}
                handleClose={() => setPartsModalOpen(false)}
                footer={
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6">Manual Parts/Materials Entries</Typography>
                        <Box sx={{ mb: 2 }} />
                        <Typography>{getManualMaterials()}</Typography>
                    </Box>
                }
            />

            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <Box sx={{ width: "100%" }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                        value={statusToTabIndex(status)}
                        onChange={(event, newValue) => navigate(`/work-orders?status=${tabIndexToStatus(newValue)}`)}
                        centered
                    >
                        <Tab label="Unassigned" {...a11yTabProps(0)} />
                        <Tab label="In progress" {...a11yTabProps(1)} />
                        <Tab label="Submitted" {...a11yTabProps(2)} />
                        <Tab label="Completed" {...a11yTabProps(3)} />
                        <Tab label="All" {...a11yTabProps(4)} />
                    </Tabs>
                </Box>

                {(!existingSitesLoaded || !exisingWorkOrdersLoaded) && (
                    <Stack direction="row" justifyContent="center" width="100%" marginTop={20}>
                        <Stack direction="column" justifyContent="center">
                            <CircularProgress size={100} />
                        </Stack>
                    </Stack>
                )}

                {existingSitesLoaded && exisingWorkOrdersLoaded && (
                    <>
                        {statusToTabIndex(status) === 0 && tabDataPageFactory("UN", 0, unassignedColumns)}
                        {statusToTabIndex(status) === 1 && tabDataPageFactory("IP", 1, inProgColumns)}
                        {statusToTabIndex(status) === 2 && tabDataPageFactory("SB", 2, submittedColumns)}
                        {statusToTabIndex(status) === 3 && tabDataPageFactory("CM", 3, completedColumns)}
                        {statusToTabIndex(status) === 4 && tabDataPageFactory("ALL", 4, allColumns)}
                    </>
                )}
            </Box>

            <CreateWorkOrderModal
                open={createModalOpen}
                handleClose={() => setCreateModalOpen(false)}
                existingSites={existingSites}
                onWorkOrderCreated={() => {}}
            />

            <ImportWalmartWorkOrderModal open={importModalOpen} handleClose={() => setImportModalOpen(false)} />
        </Box>
    );
};

export default WorkOrdersList;
